/**
 * `Intl.PluralRules`, quand le moteur JavaScript ne l'a pas.
 *
 * i18next choisit entre « 1 produit » et « 3 produits » en demandant sa
 * catégorie à `Intl.PluralRules`. Hermes ne l'expose pas sur tous les
 * appareils, et i18next se rabat alors silencieusement sur la forme plurielle
 * pour tout : « 1 produits ». Une faute de français à chaque compteur de
 * l'application, sans la moindre erreur pour le dire.
 *
 * Ce fichier comble ce trou, et lui seul : si le moteur sait déjà le faire, on
 * ne touche à rien — l'ICU de la plateforme est plus complet que nous.
 *
 * Les règles reprennent le CLDR, pour les trois langues de BUL :
 *
 * - **français** : zéro et un sont au singulier (« 0 produit », « 1 produit ») ;
 * - **malgache** : le nom ne varie pas en nombre (« vokatra iray », « vokatra
 *   telo »), mais le CLDR lui reconnaît les deux catégories comme au français —
 *   on s'y tient, les deux formes étant de toute façon écrites à l'identique ;
 * - **anglais** : seul un est au singulier.
 *
 * Toute autre langue retombe sur `other` : le repli du CLDR lui-même, et la
 * seule réponse honnête pour une langue dont on n'a pas la table.
 */
import { DEFAULT_LOCALE, matchLocale } from '@/shared/i18n/locales';
import type { Locale } from '@/shared/i18n/locales';

type Category = 'one' | 'other';

/** Vrai quand la langue met zéro et un au singulier. */
const ZERO_IS_SINGULAR: Record<Locale, boolean> = {
  fr: true,
  mg: true,
  en: false,
};

function select(locale: Locale, count: number): Category {
  if (!Number.isFinite(count)) {
    return 'other';
  }
  // Le CLDR raisonne sur la partie entière : « 1,5 produit » reste au
  // singulier en français, et « 1.5 products » au pluriel en anglais.
  const integer = Math.floor(Math.abs(count));
  if (ZERO_IS_SINGULAR[locale]) {
    return integer === 0 || integer === 1 ? 'one' : 'other';
  }
  return count === 1 ? 'one' : 'other';
}

/**
 * La forme réduite de `Intl.PluralRules` dont i18next se sert : `select` pour
 * traduire un nombre en catégorie, `resolvedOptions` pour connaître les
 * catégories possibles — c'est ce second appel qui lui dit si la langue a
 * besoin de deux formes ou d'une seule.
 */
class PluralRulesShim {
  private readonly locale: Locale;
  private readonly ordinal: boolean;

  constructor(locales?: string | Array<string>, options?: { type?: 'cardinal' | 'ordinal' }) {
    const tag = Array.isArray(locales) ? locales[0] : locales;
    this.locale = matchLocale(tag) ?? DEFAULT_LOCALE;
    this.ordinal = options?.type === 'ordinal';
  }

  select(count: number): Category {
    // Les ordinaux (« 1er », « 2e ») ne servent nulle part dans BUL : une seule
    // forme est la réponse juste, plutôt qu'une table inventée.
    return this.ordinal ? 'other' : select(this.locale, count);
  }

  resolvedOptions(): { locale: string; pluralCategories: Array<Category>; type: string } {
    const plural: Array<Category> = this.ordinal ? ['other'] : ['one', 'other'];
    return {
      locale: this.locale,
      pluralCategories: plural,
      type: this.ordinal ? 'ordinal' : 'cardinal',
    };
  }
}

/**
 * Pose la doublure si — et seulement si — le moteur n'a rien à proposer.
 *
 * À appeler avant `i18next.init` : le résolveur de pluriels est construit à
 * l'initialisation et met ses règles en cache.
 */
export function installPluralRules(): void {
  // `globalThis.Intl` est déclaré en lecture seule par TypeScript, et
  // `Intl.PluralRules` avec lui : la seule façon d'y poser quelque chose est de
  // parler au global comme à un objet ordinaire. C'est justement le propre
  // d'une prothèse — elle écrit là où la déclaration dit que rien ne manque.
  const host = globalThis as unknown as { Intl?: Record<string, unknown> };
  const intl = host.Intl ?? {};
  if (intl.PluralRules) {
    return;
  }
  intl.PluralRules = PluralRulesShim;
  host.Intl = intl;
}
